import React, { useEffect } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "app/lib/store/store";
import {
  nextStep,
  prevStep,
  addStepsData,
} from "app/lib/store/slices/steps/jointAssetsLiabilities";
import { useAddAssetsLiabilities } from "app/hooks/assetsLiabilities/useAddAssetsLiabilities";
import MainTitle from "app/components/common/typography/MainTitle";
import ContinueButton from "app/components/common/buttons/Continue";
import PreviousButtom from "app/components/common/buttons/Previous";
import ProgressBar from "app/components/common/progressbar/ProgressBar";
import InfoTitle from "app/components/common/typography/InfoTitle";
import RadioButton from "app/components/common/buttons/RadioButton";
import AddMore from "app/components/common/buttons/AddMore";
import { ASSST_LIABILITY } from "app/constants/apiEndPoints";
import toast from "react-hot-toast";
import ErrorsMessage from "app/components/common/errors/ErrorsMessage";
import { useDeleteItem } from "app/hooks/useDeleteItem";

import { JointAssetsLiabilitiesPartTwoFormData } from "app/utils/types";
import DynamicSavingsInvestments from "app/components/Individual/applicant/assetsLiabilities/DynamicSavingsInvestments";
import DynamicSavingsOtherAssets from "app/components/Individual/applicant/assetsLiabilities/DynamicSavingsOtherAssets";

const PartTwo: React.FC = () => {
  const dispatch = useDispatch();
  const { mutate: deleteItem, isPending: deleteIsPending } = useDeleteItem();
  const savedIds = useSelector(
    (state: RootState) => state.loanTypesSteps?.savedIds
  );
  const { mutateAsync: submitAssetsLiabilities, isPending } =
    useAddAssetsLiabilities();
  const { formTwo, assetLiabilityId, secoundAssetLiabilityId, ...others } =
    useSelector(
      (state: RootState) => state.jointAssetsLiabilities.stepsData
    ) || {
      formTwo: { savingAndInvestments: [], otherAssets: [] },
    };

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm<JointAssetsLiabilitiesPartTwoFormData>({
    defaultValues: {
      yourDetails: {
        savingAndInvestmentsType: "no",
        otherAssetsType: "no",
        savingAndInvestments: [{ detail: "", value: "" }],
        otherAssets: [{ detail: "", value: "" }],
      },
      coBorrowerDetails: {
        savingAndInvestmentsType: "no",
        otherAssetsType: "no",
        savingAndInvestments: [{ detail: "", value: "" }],
        otherAssets: [{ detail: "", value: "" }],
      },
    },
  });
  const savingsinvestmentsValue = watch("yourDetails.savingAndInvestmentsType");
  const cosavingsinvestmentsValue = watch(
    "coBorrowerDetails.savingAndInvestmentsType"
  );
  const otherassetsValue = watch("yourDetails.otherAssetsType");
  const cootherassetsValue = watch("coBorrowerDetails.otherAssetsType");

  const onSubmit = async (data: JointAssetsLiabilitiesPartTwoFormData) => {
    const { yourDetails, coBorrowerDetails } = data;
    const yourDetailsPayload = {
      savingAndInvestments:
        yourDetails.savingAndInvestmentsType === "yes"
          ? yourDetails.savingAndInvestments.map((item) => ({
              detail: item.detail,
              id: item.dbId,
              value: Number(item.value),
            }))
          : null,
      otherAssets:
        yourDetails.otherAssetsType === "yes"
          ? yourDetails?.otherAssets?.map((item) => ({
              detail: item.detail,
              id: item.dbId,
              value: Number(item.value),
            }))
          : null,
    };
    const coBorrowerDetailsPayload = {
      savingAndInvestments:
        coBorrowerDetails.savingAndInvestmentsType === "yes"
          ? coBorrowerDetails.savingAndInvestments.map((item) => ({
              detail: item.detail,
              ...(item.dbId && { id: item.dbId }),
              value: Number(item.value),
            }))
          : null,
      otherAssets:
        coBorrowerDetails.otherAssetsType === "yes"
          ? coBorrowerDetails.otherAssets?.map((item) => ({
              detail: item.detail,
              ...(item.dbId && { id: item.dbId }),
              value: Number(item.value),
            }))
          : null,
    };

    const request1 = {
      url: `${ASSST_LIABILITY}/${savedIds.applicantID}/second`,
      data: yourDetailsPayload,
    };
    const request2 = {
      url: `${ASSST_LIABILITY}/${savedIds.secondApplicantID}/second`,
      data: coBorrowerDetailsPayload,
    };

    try {
      const [response1, response2] = await Promise.all([
        submitAssetsLiabilities(request1),
        submitAssetsLiabilities(request2),
      ]);
      if (response1 && response2) {
        const yourOtherAssetsMapped = response1?.otherAssets?.map(
          (item: any) => ({
            dbId: item.id,
            detail: item.detail,
            value: Number(item.value),
          })
        );
        const yourSavingAndInvestmentsMapped =
          response1?.savingAndInvestments?.map((item: any) => ({
            dbId: item.id,
            detail: item.detail,
            value: Number(item.value),
          }));

        const coOtherAssetsMapped = response2?.otherAssets?.map(
          (item: any) => ({
            dbId: item.id,
            detail: item.detail,
            value: Number(item.value),
          })
        );
        const coSavingAndInvestmentsMapped =
          response2?.savingAndInvestments?.map((item: any) => ({
            dbId: item.id,
            detail: item.detail,
            value: Number(item.value),
          }));

        const savedData = {
          yourDetails: {
            ...formTwo?.yourDetails,
            savingAndInvestments:
              yourDetails.savingAndInvestmentsType === "no"
                ? []
                : yourSavingAndInvestmentsMapped,
            otherAssets:
              yourDetails.otherAssetsType === "no" ? [] : yourOtherAssetsMapped,
            otherAssetsType: yourDetails.otherAssetsType,
            savingAndInvestmentsType: yourDetails.savingAndInvestmentsType,
          },
          coBorrowerDetails: {
            ...formTwo?.coBorrowerDetails,
            savingAndInvestments:
              coBorrowerDetails.savingAndInvestmentsType === "no"
                ? []
                : coSavingAndInvestmentsMapped,
            otherAssets:
              coBorrowerDetails.otherAssetsType === "no"
                ? []
                : coOtherAssetsMapped,
            otherAssetsType: coBorrowerDetails.otherAssetsType,
            savingAndInvestmentsType:
              coBorrowerDetails.savingAndInvestmentsType,
          },
        };
        dispatch(
          addStepsData({
            ...others,
            formTwo: savedData,
          })
        );
        dispatch(nextStep());
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Something went wrong.");
    }
  };

  const { fields, append, remove } = useFieldArray({
    control,
    name: "yourDetails.savingAndInvestments",
  });

  const {
    fields: otherAssetsFields,
    append: otherAssetsAppend,
    remove: otherAssetsRemove,
  } = useFieldArray({
    control,
    name: "yourDetails.otherAssets",
  });

  const {
    fields: coFields,
    append: coAppend,
    remove: coRemove,
  } = useFieldArray({
    control,
    name: "coBorrowerDetails.savingAndInvestments",
  });

  const {
    fields: coOtherAssetsFields,
    append: coOtherAssetsAppend,
    remove: coOtherAssetsRemove,
  } = useFieldArray({
    control,
    name: "coBorrowerDetails.otherAssets",
  });

  useEffect(() => {
    if (formTwo && Object.keys(formTwo).length > 0) {
      reset({
        yourDetails: {
          ...formTwo.yourDetails,
          savingAndInvestments:
            formTwo.yourDetails?.savingAndInvestmentsType === "no"
              ? [{ detail: "", value: "" }]
              : formTwo.yourDetails.savingAndInvestments || [
                  { detail: "", value: "" },
                ],
          otherAssets:
            formTwo.yourDetails?.otherAssetsType === "no"
              ? [{ detail: "", value: "" }]
              : formTwo.yourDetails.otherAssets || [{ detail: "", value: "" }],
        },
        coBorrowerDetails: {
          ...formTwo.coBorrowerDetails,
          savingAndInvestments:
            formTwo.coBorrowerDetails?.savingAndInvestmentsType === "no"
              ? [{ detail: "", value: "" }]
              : formTwo.coBorrowerDetails.savingAndInvestments || [
                  { detail: "", value: "" },
                ],
          otherAssets:
            formTwo.coBorrowerDetails?.otherAssetsType === "no"
              ? [{ detail: "", value: "" }]
              : formTwo.coBorrowerDetails.otherAssets || [
                  { detail: "", value: "" },
                ],
        },
      });
    }
  }, [formTwo, reset]);

  return (
    <>
      <ProgressBar progress={24} MB={4} />
      <div className="w-full sm:w-[60%] lg:w-[50%] mx-auto text-center px-4 sm:px-0 mb-3">
        <MainTitle title="Saving & Investments/Other Assets" />
      </div>
      <div className="mx-auto w-internalWidth">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex mb-8 flex-col md:flex-col xl:flex-row sm:gap-4">
            <div className="flex-1">
              <InfoTitle title="Your Details" />
              <div className="text-left mb-5">
                <h6 className="font-Mulish text-nowrap text-base text-[#585C7B] mb-4">
                  Do you have any savings & investments?
                </h6>
                <div className="flex gap-5">
                  <Controller
                    name="yourDetails.savingAndInvestmentsType"
                    control={control}
                    rules={{ required: "Please select an option" }}
                    render={({ field: { value, onChange } }) => (
                      <div className="flex gap-5">
                        <RadioButton
                          id="savingsinvestments_yes"
                          name="yourDetails.savingAndInvestmentsType"
                          value="yes"
                          label="Yes"
                          checked={value === "yes"}
                          onChange={onChange}
                        />
                        <RadioButton
                          id="savingsinvestments_no"
                          name="yourDetails.savingAndInvestmentsType"
                          value="no"
                          label="No"
                          checked={value === "no"}
                          onChange={onChange}
                        />
                      </div>
                    )}
                  />
                </div>
                {errors?.yourDetails?.savingAndInvestmentsType && (
                  <ErrorsMessage
                    title={
                      errors?.yourDetails?.savingAndInvestmentsType.message
                    }
                    className="!text-left"
                  />
                )}
              </div>

              {savingsinvestmentsValue === "yes" && (
                <>
                  <DynamicSavingsInvestments
                    control={control}
                    errors={errors}
                    fields={fields}
                    deleteItem={deleteItem}
                    savingAndInvestments="yourDetails.savingAndInvestments"
                    remove={remove}
                    applicantID={savedIds.applicantID as string}
                    deleteIsPending={deleteIsPending}
                  />
                  {fields.length < 3 && (
                    <div className="flex items-center justify-end mt-6  mb-4 sm:mb-2">
                      <AddMore
                        onClick={() => append({ detail: "", value: "" })}
                      />
                    </div>
                  )}
                </>
              )}

              <div className="text-left mb-5">
                <h6 className="font-Mulish text-nowrap text-base text-[#585C7B] mb-4">
                  Do you have any other assets?
                </h6>
                <div className="flex gap-5">
                  <Controller
                    name="yourDetails.otherAssetsType"
                    control={control}
                    rules={{ required: "Please select an option" }}
                    render={({ field: { value, onChange } }) => (
                      <div className="flex gap-5">
                        <RadioButton
                          id="otherassets_yes"
                          name="yourDetails.otherAssetsType"
                          value="yes"
                          label="Yes"
                          checked={value === "yes"}
                          onChange={onChange}
                        />
                        <RadioButton
                          id="otherassets_no"
                          name="yourDetails.otherAssetsType"
                          value="no"
                          label="No"
                          checked={value === "no"}
                          onChange={onChange}
                        />
                      </div>
                    )}
                  />
                </div>
                {errors?.yourDetails?.otherAssetsType && (
                  <ErrorsMessage
                    title={errors?.yourDetails.otherAssetsType.message}
                    className="!text-left"
                  />
                )}
              </div>

              {otherassetsValue === "yes" && (
                <>
                  <DynamicSavingsOtherAssets
                    control={control}
                    errors={errors}
                    fields={otherAssetsFields}
                    deleteItem={deleteItem}
                    otherAssets="yourDetails.otherAssets"
                    remove={otherAssetsRemove}
                    applicantID={savedIds.applicantID as string}
                    deleteIsPending={deleteIsPending}
                  />

                  {otherAssetsFields.length < 3 && (
                    <div className="flex items-center justify-end mt-6  mb-6 sm:mb-2">
                      <AddMore
                        onClick={() =>
                          otherAssetsAppend({
                            detail: "",
                            value: "",
                          })
                        }
                      />
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="flex-1">
              <InfoTitle title="Co-borrower" />
              <div className="text-left mb-5">
                <h6 className="font-Mulish text-nowrap text-base text-[#585C7B] mb-4">
                  Do you have any savings & investments?
                </h6>
                <div className="flex gap-5">
                  <Controller
                    name="coBorrowerDetails.savingAndInvestmentsType"
                    control={control}
                    rules={{ required: "Please select an option" }}
                    render={({ field: { value, onChange } }) => (
                      <div className="flex gap-5">
                        <RadioButton
                          id="cosavingsinvestments_yes"
                          name="coBorrowerDetails.savingAndInvestmentsType"
                          value="yes"
                          label="Yes"
                          checked={value === "yes"}
                          onChange={onChange}
                        />
                        <RadioButton
                          id="cosavingsinvestments_no"
                          name="coBorrowerDetails.savingAndInvestmentsType"
                          value="no"
                          label="No"
                          checked={value === "no"}
                          onChange={onChange}
                        />
                      </div>
                    )}
                  />
                </div>
                {errors?.coBorrowerDetails?.savingAndInvestmentsType && (
                  <ErrorsMessage
                    title={
                      errors.coBorrowerDetails.savingAndInvestmentsType.message
                    }
                    className="!text-left"
                  />
                )}
              </div>

              {cosavingsinvestmentsValue === "yes" && (
                <>
                  <DynamicSavingsInvestments
                    control={control}
                    errors={errors}
                    fields={coFields}
                    deleteItem={deleteItem}
                    savingAndInvestments="coBorrowerDetails.savingAndInvestments"
                    remove={coRemove}
                    applicantID={savedIds?.secondApplicantID as string}
                    deleteIsPending={deleteIsPending}
                  />
                  {coFields.length < 3 && (
                    <div className="flex items-center justify-end mt-6  mb-4 sm:mb-2">
                      <AddMore
                        onClick={() => coAppend({ detail: "", value: "" })}
                      />
                    </div>
                  )}
                </>
              )}

              <div className="text-left mb-5">
                <h6 className="font-Mulish text-nowrap text-base text-[#585C7B] mb-4">
                  Do you have any other assets?
                </h6>
                <div className="flex gap-5">
                  <Controller
                    name="coBorrowerDetails.otherAssetsType"
                    control={control}
                    rules={{ required: "Please select an option" }}
                    render={({ field: { value, onChange } }) => (
                      <div className="flex gap-5">
                        <RadioButton
                          id="cootherassets_yes"
                          name="coBorrowerDetails.otherAssetsType"
                          value="yes"
                          label="Yes"
                          checked={value === "yes"}
                          onChange={onChange}
                        />
                        <RadioButton
                          id="cootherassets_no"
                          name="coBorrowerDetails.otherAssetsType"
                          value="no"
                          label="No"
                          checked={value === "no"}
                          onChange={onChange}
                        />
                      </div>
                    )}
                  />
                </div>
                {errors?.coBorrowerDetails?.otherAssetsType && (
                  <ErrorsMessage
                    title={errors?.coBorrowerDetails?.otherAssetsType.message}
                    className="!text-left"
                  />
                )}
              </div>

              {cootherassetsValue === "yes" && (
                <>
                  <DynamicSavingsOtherAssets
                    control={control}
                    errors={errors}
                    fields={coOtherAssetsFields}
                    deleteItem={deleteItem}
                    otherAssets="coBorrowerDetails.otherAssets"
                    remove={coOtherAssetsRemove}
                    applicantID={savedIds?.secondApplicantID as string}
                    deleteIsPending={deleteIsPending}
                  />
                  {coOtherAssetsFields.length < 3 && (
                    <div className="flex items-center justify-end mt-6  mb-4 sm:mb-2">
                      <AddMore
                        onClick={() =>
                          coOtherAssetsAppend({
                            detail: "",
                            value: "",
                          })
                        }
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="flex justify-between mb-6">
            <PreviousButtom
              onClick={() => {
                dispatch(prevStep());
              }}
            >
              Previous
            </PreviousButtom>
            <ContinueButton type="submit" loading={isPending}>
              Next
            </ContinueButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default PartTwo;
